// ---------------------------------------------------------------------------
// Letters round: pile construction, draw, and the vowel/consonant minimums.
//
// Two piles are drawn from without replacement. `Y` lives in the consonant
// pile, exactly as on the show, so the vowels are precisely A, E, I, O and U.
//
// The minimums (3 vowels, 4 consonants across 9 tiles) are enforced as a
// reachability check rather than as two independent caps: a pick is refused
// only when making it would leave too few slots for the *other* kind's
// outstanding minimum. That single rule is what pins the final vowel count
// between 3 and 5, and it guarantees at least one kind is always pickable, so
// no pick sequence can paint itself into a corner.
//
// `isFullAnagramOf` at the foot is the conundrum's half of the same subject:
// which strings the nine letters in front of a player are allowed to spell.
// ---------------------------------------------------------------------------
import { CONSONANT_PILE, LETTERS_MIN_CONSONANTS, LETTERS_MIN_VOWELS, LETTERS_TILE_COUNT, VOWEL_PILE, } from './constants.js';
import { canBuildFromTiles, normalizeWord } from './validation.js';
const VOWELS = 'AEIOU';
const PICK_ORDER = ['vowel', 'consonant'];
/** Fisher-Yates. Clamped so a pathological `random` returning 1 cannot escape. */
function shuffle(items, random) {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.min(i, Math.floor(random() * (i + 1)));
        const held = out[i];
        out[i] = out[j];
        out[j] = held;
    }
    return out;
}
function expandPile(counts) {
    const out = [];
    for (const [letter, count] of Object.entries(counts)) {
        for (let i = 0; i < count; i += 1)
            out.push(letter);
    }
    return out;
}
function countKind(tiles, kind) {
    let n = 0;
    for (const tile of tiles) {
        if (tile.kind === kind)
            n += 1;
    }
    return n;
}
/** Expand VOWEL_PILE / CONSONANT_PILE into shuffled arrays of single letters. */
export function buildVowelPile(random = Math.random) {
    return shuffle(expandPile(VOWEL_PILE), random);
}
export function buildConsonantPile(random = Math.random) {
    return shuffle(expandPile(CONSONANT_PILE), random);
}
/** Pop one letter. Mutates the pile. Returns null when the pile is empty. */
export function drawLetter(pile) {
    if (pile.length === 0)
        return null;
    return pile.pop() ?? null;
}
export function letterKindOf(letter) {
    return VOWELS.includes(letter.trim().toUpperCase()) ? 'vowel' : 'consonant';
}
/**
 * Whether the chooser may still pick this kind, given what is already on the
 * board. Enforces min 3 vowels and min 4 consonants across 9 tiles, which
 * pins the vowel count between 3 and 5.
 */
export function canPick(tiles, kind) {
    if (tiles.length >= LETTERS_TILE_COUNT)
        return false;
    const vowels = countKind(tiles, 'vowel') + (kind === 'vowel' ? 1 : 0);
    const consonants = countKind(tiles, 'consonant') + (kind === 'consonant' ? 1 : 0);
    const slotsLeft = LETTERS_TILE_COUNT - (vowels + consonants);
    if (slotsLeft < 0)
        return false;
    const outstanding = Math.max(0, LETTERS_MIN_VOWELS - vowels) +
        Math.max(0, LETTERS_MIN_CONSONANTS - consonants);
    return outstanding <= slotsLeft;
}
/** Kinds the chooser is still allowed to pick. */
export function allowedPicks(tiles) {
    return PICK_ORDER.filter((kind) => canPick(tiles, kind));
}
/**
 * Whether `word` uses exactly the letters of `scramble`, every one of them and
 * nothing else.
 *
 * This is the conundrum's answer shape. `canBuildFromTiles` is most of it and
 * does the multiplicity work; the length equality is what turns "buildable from"
 * into "an anagram of", because a nine-letter scramble that spells a six-letter
 * word is not an unscrambling of it.
 *
 * It lives here, pure and dictionary-free, because three call sites rule on the
 * same question — the socket handler, the judge, and the chat censor — and a
 * rule that is retyped in three places is a rule that eventually disagrees with
 * itself. Whether the result is also a *word* is the dictionary's business and
 * deliberately not asked here.
 */
export function isFullAnagramOf(word, scramble) {
    const normalized = normalizeWord(word);
    const letters = normalizeWord(scramble);
    if (normalized.length === 0)
        return false;
    if (normalized.length !== letters.length)
        return false;
    return canBuildFromTiles(normalized, [...letters]);
}
/**
 * Fill the board to nine tiles at random while respecting the minimums.
 * Used when the selection budget expires. Mutates both piles.
 *
 * Every pick goes through `canPick`, so an outstanding minimum is forced the
 * moment it becomes tight and the finished board is always legal. A pile with
 * nothing left in it is skipped rather than throwing; with 67 and 74 tiles that
 * cannot happen inside one round, but a caller reusing a pile deserves an
 * answer rather than a crash.
 */
export function autoFillLetters(tiles, vowelPile, consonantPile, random = Math.random) {
    const board = tiles.slice();
    while (board.length < LETTERS_TILE_COUNT) {
        const viable = allowedPicks(board).filter((kind) => (kind === 'vowel' ? vowelPile : consonantPile).length > 0);
        if (viable.length === 0)
            break;
        const index = Math.min(viable.length - 1, Math.floor(random() * viable.length));
        const kind = viable[index];
        const letter = drawLetter(kind === 'vowel' ? vowelPile : consonantPile);
        if (letter === null)
            break;
        board.push({ letter, kind });
    }
    return board;
}
